import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'agent',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('📤 Signup request payload:', formData);
      // Client-side validation for password length
      if (!formData.password || formData.password.length < 8) {
        setError('Şifre en az 8 karakter olmalıdır.');
        setLoading(false);
        return;
      }
      const response = await authService.signup(formData);
      console.log('📥 Signup response:', response);
      if (response.success) {
        setSuccess('Kayıt başarılı! Lütfen hesabınızın onaylanmasını bekleyin.');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        // If validation errors exist, show first message
        if (response.errors && response.errors.length > 0) {
          const messages = response.errors.map((e: any) => e.msg).join(' ');
          setError(messages);
        } else {
          setError(response.message || 'Kayıt başarısız.');
        }
      }
    } catch (error) {
      setError('Kayıt sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '480px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '24px',
            padding: '48px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Title */}
          <Typography
            sx={{
              color: '#1a1a2e',
              fontWeight: 700,
              fontSize: '32px',
              textAlign: 'center',
              marginBottom: '12px',
              marginTop: '8px',
            }}
          >
            Kayıt Olun
          </Typography>

          {/* Subtitle */}
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '15px',
              textAlign: 'center',
              marginBottom: '32px',
              lineHeight: 1.6,
            }}
          >
            Yeni bir hesap oluşturmak için bilgilerinizi girin.
          </Typography>

          <form onSubmit={handleSubmit}>
            {/* First Name Field */}
            <Box sx={{ marginBottom: '20px' }}>
              <TextField
                fullWidth
                label="Ad"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '56px',
                    borderRadius: '12px',
                    backgroundColor: '#F9FAFB',
                    transition: 'all 0.3s ease',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                      borderWidth: '2px',
                    },
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                      '& fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      '& fieldset': {
                        borderColor: '#667eea',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#6B7280',
                    fontSize: '15px',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#667eea',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '16px 18px',
                    fontSize: '15px',
                    color: '#1F2937',
                  },
                }}
              />
            </Box>

            {/* Last Name Field */}
            <Box sx={{ marginBottom: '20px' }}>
              <TextField
                fullWidth
                label="Soyad"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '56px',
                    borderRadius: '12px',
                    backgroundColor: '#F9FAFB',
                    transition: 'all 0.3s ease',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                      borderWidth: '2px',
                    },
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                      '& fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      '& fieldset': {
                        borderColor: '#667eea',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#6B7280',
                    fontSize: '15px',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#667eea',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '16px 18px',
                    fontSize: '15px',
                    color: '#1F2937',
                  },
                }}
              />
            </Box>

            {/* Email Field */}
            <Box sx={{ marginBottom: '20px' }}>
              <TextField
                fullWidth
                label="E-posta"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '56px',
                    borderRadius: '12px',
                    backgroundColor: '#F9FAFB',
                    transition: 'all 0.3s ease',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                      borderWidth: '2px',
                    },
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                      '& fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      '& fieldset': {
                        borderColor: '#667eea',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#6B7280',
                    fontSize: '15px',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#667eea',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '16px 18px',
                    fontSize: '15px',
                    color: '#1F2937',
                  },
                }}
              />
            </Box>

            {/* Password Field */}
            <Box sx={{ marginBottom: '16px' }}>
              <TextField
                fullWidth
                label="Parola"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '56px',
                    borderRadius: '12px',
                    backgroundColor: '#F9FAFB',
                    transition: 'all 0.3s ease',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                      borderWidth: '2px',
                    },
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                      '& fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      '& fieldset': {
                        borderColor: '#667eea',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#6B7280',
                    fontSize: '15px',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#667eea',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '16px 18px',
                    fontSize: '15px',
                    color: '#1F2937',
                  },
                }}
              />
            </Box>
            {formData.password && formData.password.length < 8 && (
              <Typography color="error" variant="body2" sx={{ marginBottom: '16px' }}>
                Parola en az 8 karakter olmalıdır.
              </Typography>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              disabled={loading || !formData.firstName || !formData.lastName || !formData.email || !formData.password || formData.password.length < 8}
              sx={{
                height: '56px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontSize: '16px',
                fontWeight: 600,
                textTransform: 'none',
                color: '#fff',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  background: '#E5E7EB',
                  color: '#9CA3AF',
                  boxShadow: 'none',
                },
                marginBottom: '24px',
                marginTop: '8px',
              }}
            >
              {loading ? 'Kayıt yapılıyor...' : 'Kayıt Ol'}
            </Button>
          </form>
        </Box>
      </Box>
    </Container>
  );
};

export default Signup;